// Importing BrowserRouter, Routes, Route, and useLocation from react-router-dom for routing and navigation
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";

// Importing always-needed components (loaded immediately)
import NavigationComponent from './components/Nav'; // Navigation component
import SearchHeader from './components/SearchHeader'; // Global search component
import Footer from "./components/Footer"; // Footer component displayed at the bottom of the page
import Login from "./components/Login"; // Component for user login functionality
import ProtectedRoute from "./components/common/ProtectedRoute"; // Component for protected routes
import { AuthProvider } from "./context/AuthContext"; // Component for protected routes
import ScrollToTop from "./components/common/ScrollToTop"; // Component to scroll to top on route change
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary"; // Global error boundary

// Import sidebar components
import { AppSidebar } from "./components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./components/ui/breadcrumb";
import { Separator } from "./components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./components/ui/sidebar";

// Lazy-loaded components (loaded only when needed)
const QuoteComponent = lazy(() => import("./components/Quote")); // Component to display and manage quotes
const OrderComponent = lazy(() => import("./components/Order")); // Component to display and manage orders
const ArchivedOrdersComponent = lazy(() => import("./components/ArchivedOrders")); // Component to display and manage archived orders
const CustomerComponent = lazy(() => import("./components/Customer")); // Component to display and manage customer information
const NotFoundComponent = lazy(() => import("./components/NotFound")); // Component to handle and display 404 "Not Found" errors
const EmailDisplay = lazy(() => import("./components/EmailDisplay")); // Component to display and manage email information
const DashboardComponent = lazy(() => import("./components/Dashboard")); // Component to display all reports
const AnalyticsComponent = lazy(() => import("./components/Analytics")); // Component for analytics and reporting
const AboutUsComponent = lazy(() => import("./components/AboutUs")); 
const ServicesComponent = lazy(() => import("./components/Services"));
const TeamComponent = lazy(() => import("./components/Team")); // Component for Our Team page
const CareersComponent = lazy(() => import("./components/Careers")); // Component for Careers page
const OnboardingComponent = lazy(() => import("./components/Onboarding"));
const SettingsComponent = lazy(() => import("./components/ProductionSettings")); // Component for production settings management
const QuoteResponse = lazy(() => import("./components/QuoteResponse")); // Component for customer quote responses via email

// Loading fallback component - invisible to let components handle their own loading
const LoadingFallback = () => null;

function AppContent() {
  // Get the current location to determine the current route
  const location = useLocation();
  
  // Determine if Navigation and Footer should be hidden
  // Hide Navigation and Footer on the Login page ("/") and on the quote-response page (public customer page)
  const hideHeaderAndFooter = location.pathname === "/" || location.pathname === "/quote-response" || !["/", "/dashboard", "/analytics", "/quotes", "/orders", "/orders/archived", "/customers", "/email_display", "/about", "/services", "/team", "/careers", "/onboarding", "/settings"].includes(location.pathname);

  // Determine if sidebar should be shown - only on protected routes
  const showSidebar = !hideHeaderAndFooter && location.pathname !== "/";

  // Get breadcrumb info based on current path
  const getBreadcrumbInfo = () => {
    const pathMap = {
      "/dashboard": { section: "Main", page: "Dashboard" },
      "/analytics": { section: "Main", page: "Analytics & Reporting" },
      "/quotes": { section: "Main", page: "Quotes" },
      "/orders": { section: "Main", page: "Orders" },
      "/orders/archived": { section: "Main", page: "Archived Orders" },
      "/customers": { section: "Main", page: "Customers" },
      "/email_display": { section: "Main", page: "Emails" },
      "/onboarding": { section: "Main", page: "Onboarding" },
      "/settings": { section: "Settings", page: "Production Settings" },
      "/about": { section: "Info", page: "About Us" },
      "/services": { section: "Info", page: "Services" },
      "/team": { section: "Info", page: "Our Team" },
      "/careers": { section: "Info", page: "Careers" },
    };
    return pathMap[location.pathname] || { section: "Main", page: "Dashboard" };
  };

  const breadcrumbInfo = getBreadcrumbInfo();

  return (
    <AuthProvider>
      <TooltipProvider>
        {/* ScrollToTop component to handle automatic scrolling on route changes */}
        <ScrollToTop />
      
        {showSidebar ? (
          // Layout with sidebar for protected routes - following shadcn pattern
          <SidebarProvider defaultOpen={false}>
            <AppSidebar />
            <SidebarInset>
            <main className="flex flex-1 flex-col min-h-screen">
              {/* Top navigation bar */}
              <div className="app-header" style={{ background: 'white', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                <NavigationComponent />
              </div>

              {/* Sidebar header with breadcrumb */}
              <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4" style={{ background: '#ed1d35' }}>
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="mr-2 h-4" />
                <Breadcrumb>
                  <BreadcrumbList className="text-white">
                    <BreadcrumbItem className="hidden md:block text-white">
                      <BreadcrumbLink href="#" className="text-white">
                        {breadcrumbInfo.section}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator className="hidden md:block text-white" />
                    <BreadcrumbItem className="text-white">
                      <BreadcrumbPage className="text-white">{breadcrumbInfo.page}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </header>

              {/* Main content area */}
              <div className="flex flex-1 flex-col">
                <div className="flex-1">
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                    {/* Route for Login page */}
                    <Route path="/" element={<Login />} />

                    {/* Route for Dashboard page */}
                    <Route path="/dashboard" element={<ProtectedRoute><DashboardComponent /></ProtectedRoute>} />
                    
                    {/* Route for Analytics & Reporting page */}
                    <Route path="/analytics" element={<ProtectedRoute><AnalyticsComponent /></ProtectedRoute>} />
                    
                    {/* Route for QuoteComponent page */}
                    <Route path="/quotes" element={<ProtectedRoute><QuoteComponent /></ProtectedRoute>} />
                    
                    {/* Route for OrderComponent page */}
                    <Route path="/orders" element={<ProtectedRoute><OrderComponent /></ProtectedRoute>} />
                    
                    {/* Route for ArchivedOrdersComponent page */}
                    <Route path="/orders/archived" element={<ProtectedRoute><ArchivedOrdersComponent /></ProtectedRoute>} />
                    
                    {/* Route for CustomerComponent page */}
                    <Route path="/customers" element={<ProtectedRoute><CustomerComponent /></ProtectedRoute>} />

                    {/* Route for Email Display page */}
                    <Route path="/email_display" element={<ProtectedRoute><EmailDisplay /></ProtectedRoute>} />
                    
                    {/* Route for customer quote responses via email (public, no auth required) */}
                    <Route path="/quote-response" element={<QuoteResponse />} />

                    {/* Route for AboutUsComponent page */}
                    <Route path="/about" element={<AboutUsComponent />} />

                    {/* Route for ServicesComponent page */}
                    <Route path="/services" element={<ServicesComponent />} />

                    {/* Route for TeamComponent page */}
                    <Route path="/team" element={<TeamComponent />} />

                    {/* Route for CareersComponent page */}
                    <Route path="/careers" element={<CareersComponent />} />

                    {/* Route for Onboarding page*/}
                    <Route path="/onboarding" element={<ProtectedRoute><OnboardingComponent /></ProtectedRoute>} />

                    {/* Route for Settings page*/}
                    <Route path="/settings" element={<ProtectedRoute><SettingsComponent /></ProtectedRoute>} />
                    
                    {/* Catch-all route to display NotFoundComponent for any undefined routes */}
                    <Route path="*" element={<NotFoundComponent />} />
                    </Routes>
                  </Suspense>
                </div>

                {/* Footer */}
                <Footer />
              </div>
            </main>
            </SidebarInset>
          </SidebarProvider>
        ) : (
          // Layout without sidebar for public routes
          <div className="flex flex-col min-h-screen">
            {/* Only show Navigation and Search if not on Login or NotFound pages */}
            {!hideHeaderAndFooter && (
              <div className="app-header" style={{ background: 'white', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                {/* Navigation Component */}
                <NavigationComponent />
              </div>
            )}

            {/* Main content area */}
            <div className="flex-1">
              {/* Define the routes for the application */}
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                {/* Route for Login page */}
                <Route path="/" element={<Login />} />

                {/* Route for Dashboard page */}
                <Route path="/dashboard" element={<ProtectedRoute><DashboardComponent /></ProtectedRoute>} />
                
                {/* Route for Analytics & Reporting page */}
                <Route path="/analytics" element={<ProtectedRoute><AnalyticsComponent /></ProtectedRoute>} />
                
                {/* Route for QuoteComponent page */}
                <Route path="/quotes" element={<ProtectedRoute><QuoteComponent /></ProtectedRoute>} />
                
                {/* Route for OrderComponent page */}
                <Route path="/orders" element={<ProtectedRoute><OrderComponent /></ProtectedRoute>} />
                
                {/* Route for ArchivedOrdersComponent page */}
                <Route path="/orders/archived" element={<ProtectedRoute><ArchivedOrdersComponent /></ProtectedRoute>} />
                
                {/* Route for CustomerComponent page */}
                <Route path="/customers" element={<ProtectedRoute><CustomerComponent /></ProtectedRoute>} />

                {/* Route for Email Display page */}
                <Route path="/email_display" element={<ProtectedRoute><EmailDisplay /></ProtectedRoute>} />
                
                {/* Route for customer quote responses via email (public, no auth required) */}
                <Route path="/quote-response" element={<QuoteResponse />} />

                {/* Route for AboutUsComponent page */}
                <Route path="/about" element={<AboutUsComponent />} />

                {/* Route for ServicesComponent page */}
                <Route path="/services" element={<ServicesComponent />} />

                {/* Route for TeamComponent page */}
                <Route path="/team" element={<TeamComponent />} />

                {/* Route for CareersComponent page */}
                <Route path="/careers" element={<CareersComponent />} />

                {/* Route for Onboarding page*/}
                <Route path="/onboarding" element={<ProtectedRoute><OnboardingComponent /></ProtectedRoute>} />

                {/* Route for Settings page*/}
                <Route path="/settings" element={<ProtectedRoute><SettingsComponent /></ProtectedRoute>} />
                
                {/* Catch-all route to display NotFoundComponent for any undefined routes */}
                <Route path="*" element={<NotFoundComponent />} />
                </Routes>
              </Suspense>
            </div>

            {/* Only show Footer if not on Login or NotFound pages */}
            {!hideHeaderAndFooter && <Footer />}
          </div>
        )}
        
        {/* Sonner Toast Container */}
        <Toaster 
          position="top-center" 
          theme="light"
        />
      </TooltipProvider>
    </AuthProvider>
  );
}


export default function App() {
  return (
    <ErrorBoundary>
      <Router>
        {/* Render the main app content within the Router context */}
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
}